import { Suspense } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsRCFOffer } from '@helpers/FormalOffers/getIsRCFOffer';
import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { H3, Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { reduxForm } from 'redux-form';

import styles from './ConfirmForm.module.css';
import NextRepayment from './nextRepayment/NextRepayment';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import {
    SELECT_DRAW_DOWN_FUNDING_METHOD,
    SELECT_DRAW_DOWN_REPAYMENT_METHOD
} from '../../../drawDown/DrawDown.actions';
import {
    getOutstandingBalanceOnCurrentDay,
    getDrawDownAmount
} from '../../../drawDown/DrawDownSelectors';
import { SelectPaymentMethod } from '../../../earlyRepayment/confirmRepayment/SelectPaymentMethod';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { ViewNewPaymentPlan } from '../../../multiLoanDashboard/dashboardWidgets/RCFWithdrawFunds/ViewNewPaymentPlan';
import { ViewPaymentPlan } from '../../../myOffers/approvedView/RCFApprovedView/ViewPaymentPlan';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import RBLConfirmFundingInfo from '../../../revenueBasedLoan/drawdown/ConfirmFundingInfo';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import PairItemContainer from '../../../ui/pairItemContainer/PairItemContainer';
import { CONFIRM_FUNDING } from '../../Funding.actions';
import messages from '../ConfirmFundingView.messages';

const mapStateToProps = (state: RootState) => {
    const outstandingBalanceOnCurrentDay =
        getOutstandingBalanceOnCurrentDay(state);
    const withdrawalAmount = getDrawDownAmount(state);

    return {
        drawdownState: state.drawDown,
        outstandingBalanceOnCurrentDay,
        withdrawalAmount,
        initialValues: {
            payment_to: state.drawDown.selectedFunding,
            payment_from: state.drawDown.selectedRepayment
        }
    };
};

const confirmDrawdownForm = {
    form: 'confirmDrawdown',
    enableReinitialize: true
};

type TSelectedFunding = {
    type: string;
    value: string;
    cardLastDigits: string;
    issuer: string;
    brand: string;
    label: string;
    bankDetails: {
        account_number: string;
        sort_code: string;
    };
};

type TSelectedRepayment = {
    type: string;
    value: string;
    cardLastDigits: string;
    issuer: string;
    brand: string;
    label: string;
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    confirm: (amount: number, offerId: string, sweep: number) =>
        dispatch({
            type: CONFIRM_FUNDING,
            amount,
            offerId,
            sweep
        }),
    showSchedule: () => dispatch({ type: SHOW_REPAYMENT_SCHEDULE_MODAL }),
    selectDrawdownRepaymentMethod: (selectedRepayment?: TSelectedRepayment) =>
        dispatch({
            type: SELECT_DRAW_DOWN_REPAYMENT_METHOD,
            selectedRepayment
        }),
    selectDrawdownFundingMethod: (selectedFunding?: TSelectedFunding) =>
        dispatch({
            type: SELECT_DRAW_DOWN_FUNDING_METHOD,
            selectedFunding
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type TProps = {
    drawdownAmount: number;
    isTopUp: boolean;
    offerId: string;
    selectedInputAmount: number;
    selectedSweepPercentage: number;
    formalOffer: TFormalOffer;
    outstandingAccount: TAccount | undefined;
};

const Confirm = ({
    confirm,
    showSchedule,
    drawdownState,
    selectDrawdownFundingMethod,
    selectDrawdownRepaymentMethod,
    outstandingBalanceOnCurrentDay,
    drawdownAmount,
    selectedInputAmount,
    offerId,
    isTopUp,
    selectedSweepPercentage,
    formalOffer,
    outstandingAccount
}: TProps & PropsFromRedux) => {
    const totalOutstandingBalance =
        outstandingBalanceOnCurrentDay + drawdownAmount;

    return (
        <>
            <MainSectionWrapper className="pt-0">
                {selectedSweepPercentage ? (
                    <RBLConfirmFundingInfo
                        amount={selectedInputAmount}
                        sweep={selectedSweepPercentage}
                    />
                ) : (
                    <PairItemContainer>
                        <AmountSummary
                            selectedInputAmount={selectedInputAmount}
                            summaryTitle={<SummaryTitle isTopUp={isTopUp} />}
                            summaryBreakdown={
                                <SummaryBreakdown
                                    isTopUp={isTopUp}
                                    totalOutstandingBalance={
                                        totalOutstandingBalance
                                    }
                                />
                            }
                        />

                        <div className="box-border p-m min-l:p-2xl">
                            <NextRepayment />
                            <ErrorBoundary
                                fallback={() => <FallbackErrorModal />}
                            >
                                <Suspense
                                    fallback={<Skeleton className="h-[20px]" />}
                                >
                                    <ShowNextRepaymentButton
                                        drawdownAmount={drawdownAmount}
                                        totalOutstandingBalanceAfterDrawdown={
                                            totalOutstandingBalance
                                        }
                                        formalOffer={formalOffer}
                                        showRepaymentSchedule={showSchedule}
                                        outstandingAccount={outstandingAccount}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </div>
                    </PairItemContainer>
                )}

                <div className={styles.choosePayment}>
                    <div className={styles.ChoosePaymentTo}>
                        <form autoComplete="off">
                            <div className={styles.choosePaymentTo}>
                                <label>
                                    <FormattedMessage {...messages.paidInTo} />
                                </label>
                                <SelectPaymentMethod
                                    name="payment_to"
                                    options={
                                        drawdownState.fundingPaymentOptions
                                    }
                                    onChange={() =>
                                        selectDrawdownFundingMethod()
                                    }
                                    data-testid="payment-to"
                                />
                            </div>

                            <div className={styles.choosePaymentFrom}>
                                <div
                                    className={styles.choosePaymentFromContent}
                                >
                                    <label>
                                        <FormattedMessage
                                            {...messages.repaidFrom}
                                        />
                                    </label>

                                    {getCountry() === 'DE' ? (
                                        <div
                                            className={
                                                styles.choosePaymentFromDeText
                                            }
                                        >
                                            <FormattedMessage
                                                {...messages.repaymentFromExplanation}
                                            />
                                        </div>
                                    ) : (
                                        <SelectPaymentMethod
                                            name="payment_from"
                                            options={
                                                drawdownState.repaymentOptions
                                            }
                                            onChange={() =>
                                                selectDrawdownRepaymentMethod()
                                            }
                                            data-testid="payment-from"
                                        />
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className={styles.newFundingConfirmButton}>
                        <Button
                            variant="success"
                            onClick={() =>
                                confirm(
                                    drawdownAmount,
                                    offerId,
                                    selectedSweepPercentage
                                )
                            }
                            size="large"
                            data-action-name="Confirm funding"
                        >
                            <FormattedMessage {...messages.confirm} />
                        </Button>
                    </div>
                </div>
            </MainSectionWrapper>
        </>
    );
};

const AmountSummary = ({
    selectedInputAmount,
    summaryBreakdown,
    summaryTitle
}: {
    selectedInputAmount: number;
    summaryBreakdown: React.ReactNode;
    summaryTitle: React.ReactNode;
}) => {
    return (
        <div
            className="box-border p-m min-l:p-2xl"
            data-testid="selected-amount"
        >
            <H3 className="mb-m text-m font-med min-l:mb-xl">{summaryTitle}</H3>
            <div className="mb-m min-l:mb-xl">
                <MoneyWithDecimals
                    className="text-2xl min-l:text-5xl min-l:leading-[48px]"
                    value={selectedInputAmount}
                    decimalWithSeparatorClassName="min-l:text-xl"
                />
            </div>
            <div>{summaryBreakdown}</div>
        </div>
    );
};

const SummaryTitle = ({ isTopUp }: { isTopUp: boolean }) => {
    if (isTopUp) {
        return <FormattedMessage {...messages.summaryTitleTopUp} />;
    }
    return <FormattedMessage {...messages.summaryTitleDrawDown} />;
};

const SummaryBreakdown = ({
    isTopUp,
    totalOutstandingBalance
}: {
    isTopUp: boolean;
    totalOutstandingBalance: number;
}) => {
    const formattedOutstandingBalance = (
        <span>
            <FormattedCurrency value={totalOutstandingBalance} />
        </span>
    );
    if (isTopUp) {
        return (
            <FormattedMessage
                {...messages.summaryBreakdownTopUp}
                values={{
                    newBalance: formattedOutstandingBalance
                }}
            />
        );
    }

    return (
        <FormattedMessage
            {...messages.summaryBreakdownDrawDown}
            values={{
                newBalance: formattedOutstandingBalance
            }}
        />
    );
};

const ShowNextRepaymentButton = ({
    totalOutstandingBalanceAfterDrawdown,
    drawdownAmount,
    formalOffer,
    showRepaymentSchedule,
    outstandingAccount
}: {
    totalOutstandingBalanceAfterDrawdown: number;
    drawdownAmount: number;
    formalOffer: TFormalOffer;
    showRepaymentSchedule: () => void;
    outstandingAccount: TAccount | undefined;
}) => {
    if (getIsRCFOffer(formalOffer)) {
        const isFundedRCF = formalOffer.is_funded;
        const formalOfferAccessPeriod =
            outstandingAccount?.formal_offer_access_period;

        if (isFundedRCF && formalOfferAccessPeriod)
            return (
                <ViewNewPaymentPlan
                    accessPeriod={formalOfferAccessPeriod}
                    drawdownAmount={drawdownAmount}
                    newOutstandingBalance={totalOutstandingBalanceAfterDrawdown}
                />
            );

        return (
            <ViewPaymentPlan
                drawdownAmount={drawdownAmount}
                offer={formalOffer}
            />
        );
    }

    return (
        <div>
            <span className={styles.link} onClick={showRepaymentSchedule}>
                <FormattedMessage {...messages.repaymentBreakdown} />
            </span>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<any, TProps & PropsFromRedux>(confirmDrawdownForm)(Confirm));
